import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const MonthPicker = ({ value, onChange, disabled, minDate, maxDate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const dropdownRef = useRef(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Parse current value
  const [selectedYear, selectedMonth] = value ? value.split('-').map(Number) : [new Date().getFullYear(), new Date().getMonth()];

  useEffect(() => {
    if (value) {
      const [year] = value.split('-').map(Number);
      setCurrentYear(year);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMonthSelect = (year, month) => {
    const monthString = `${year}-${String(month + 1).padStart(2, '0')}`;
    onChange(monthString);
    setIsOpen(false);
  };

  const handleYearChange = (direction) => {
    const nextYear = direction === 'next' ? currentYear + 1 : currentYear - 1;
    
    // Guard against out of range years if constraints exist
    if (minDate) {
      const minYear = parseInt(minDate.split('-')[0]);
      if (nextYear < minYear) return;
    }
    if (maxDate) {
      const maxYear = parseInt(maxDate.split('-')[0]);
      if (nextYear > maxYear) return;
    }
    
    setCurrentYear(nextYear);
  };

  const isMonthDisabled = (year, monthIndex) => {
    const dateStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;
    return false;
  };

  const formatDisplayValue = () => {
    if (!value) return 'Select Month';
    const [year, month] = value.split('-').map(Number);
    return `${months[month - 1]} ${year}`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all duration-300 font-bold text-slate-700 cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed flex items-center justify-between gap-2 shadow-sm"
      >
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-blue-500" />
          <span className="text-sm">{formatDisplayValue()}</span>
        </div>
        <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-3 bg-white border border-slate-100 rounded-3xl shadow-2xl shadow-blue-900/10 z-50 min-w-72 overflow-hidden animate-fadeIn">
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-slate-50/50 border-b border-slate-100">
            <button
              type="button"
              onClick={() => handleYearChange('prev')}
              disabled={minDate && currentYear <= parseInt(minDate.split('-')[0])}
              className="p-2 hover:bg-white rounded-xl text-slate-400 hover:text-blue-600 transition-all border border-transparent hover:border-slate-100 shadow-sm disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="font-black text-slate-900 text-lg tracking-tight">{currentYear}</span>
            <button
              type="button"
              onClick={() => handleYearChange('next')}
              disabled={maxDate && currentYear >= parseInt(maxDate.split('-')[0])}
              className="p-2 hover:bg-white rounded-xl text-slate-400 hover:text-blue-600 transition-all border border-transparent hover:border-slate-100 shadow-sm disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Month Grid */}
          <div className="p-4 bg-white">
            <div className="grid grid-cols-3 gap-3">
              {months.map((month, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => handleMonthSelect(currentYear, index)}
                  disabled={isMonthDisabled(currentYear, index)}
                  className={`px-3 py-3 text-xs font-bold rounded-2xl transition-all ${
                    selectedYear === currentYear && selectedMonth === index + 1
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-blue-600'
                  } disabled:opacity-20 disabled:cursor-not-allowed disabled:grayscale-[0.5]`}
                >
                  {month.slice(0, 3).toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthPicker;
